/**
 * Item master API (M1): items, groups, UoMs and product trees.
 * Like `api/accounting.ts` this layer only reads and calls RPCs — every rule
 * (SKU uniqueness, unit factors, BOM cycles, cost roll-up) lives in SQL.
 */
import type { AslenClient, Json, Tables, TablesInsert, Views } from '../db';
import type { ItemStatus, ItemType } from '../constants';
import { toDb } from '../money';
import { trFold } from '../text';
import { toAppError, unwrap } from './errors';

export type Item = Tables<'items'>;
export type ItemListRow = Views<'v_items_list'>;
export type ItemGroup = Tables<'item_groups'>;
export type ItemUom = Tables<'item_uoms'>;
export type ItemUomOverrideRow = Views<'v_item_uom_overrides'>;
export type WhereUsedRow = Views<'v_item_where_used'>;
export type Bom = Tables<'boms'>;
export type BomLine = Tables<'bom_lines'>;
export type BomOperation = Tables<'bom_operations'>;

// ── items ───────────────────────────────────────────────────────────────────
export interface ItemListFilters {
  /** Omitted means every status; W-04 defaults to active + draft. */
  status?: ItemStatus[];
  type?: ItemType[];
  itemGroupId?: string;
  /** Free text; folded the way `items.search_key` is generated. */
  search?: string;
  limit?: number;
}

/**
 * The W-04 grid and every item picker. `v_items_list` already carries the
 * on-hand figures, the stock status, the BOM versions and `can_delete`, so a
 * list never needs a second query — and `standard_cost_stock_unit` comes back
 * null for a role that may not see costs (the screen still decides "gizli" by
 * role, not by null).
 */
export async function listItems(client: AslenClient, companyId: string, f: ItemListFilters = {}): Promise<ItemListRow[]> {
  let q = client.from('v_items_list').select('*').eq('company_id', companyId).order('type_sort').order('sku');
  if (f.status?.length) q = q.in('status', f.status);
  if (f.type?.length) q = q.in('type', f.type);
  if (f.itemGroupId) q = q.eq('item_group_id', f.itemGroupId);
  const term = trFold(f.search).trim();
  if (term) q = q.ilike('search_key', `*${term.replace(/[,()*\\]/g, ' ').trim()}*`);
  if (f.limit) q = q.limit(f.limit);
  return unwrap(await q);
}

export type ItemDetail = Item & { uoms: ItemUom[]; group: Pick<ItemGroup, 'id' | 'name' | 'variant_keys'> | null };

/** W-05's header and Genel tab: the row plus its UoM ladder and its group. */
export async function getItem(client: AslenClient, id: string): Promise<ItemDetail> {
  return unwrap(
    await client
      .from('items')
      .select('*, uoms:item_uoms(*), group:item_groups(id, name, variant_keys)')
      .eq('id', id)
      .is('deleted_at', null)
      .order('sort', { referencedTable: 'item_uoms' })
      .single(),
  ) as unknown as ItemDetail;
}

/** One row of `v_items_list` — what a scan or a deep link resolves to. */
export async function getItemListRow(client: AslenClient, companyId: string, id: string): Promise<ItemListRow | null> {
  const rows = unwrap(await client.from('v_items_list').select('*').eq('company_id', companyId).eq('id', id).limit(1));
  return rows[0] ?? null;
}

export async function listItemUoms(client: AslenClient, itemId: string): Promise<ItemUom[]> {
  return unwrap(await client.from('item_uoms').select('*').eq('item_id', itemId).order('sort').order('unit'));
}

/** Settings → Birimler → "Ürüne özel dönüşümler": items that differ from the company defaults. */
export async function listItemUomOverrides(client: AslenClient, companyId: string): Promise<ItemUomOverrideRow[]> {
  return unwrap(await client.from('v_item_uom_overrides').select('*').eq('company_id', companyId).order('sku'));
}

/** W-05 → Ürün ağacı → "Nerede kullanılıyor": the BOMs this item is a component of. */
export async function listWhereUsed(client: AslenClient, itemId: string): Promise<WhereUsedRow[]> {
  return unwrap(await client.from('v_item_where_used').select('*').eq('component_item_id', itemId).order('parent_sku'));
}

/** One UoM row of the `p_uoms` array `save_item` replaces the ladder with. */
export interface ItemUomInput {
  unit: string;
  /** Decimal text; the base unit's factor is forced to 1 by the RPC. */
  factorToBase: string;
  isPurchaseDefault?: boolean;
  isSalesDefault?: boolean;
  isOrderMultiple?: boolean;
  isCountable?: boolean;
  lengthCm?: string | null;
  widthCm?: string | null;
  heightCm?: string | null;
  grossWeightKg?: string | null;
  barcode?: string | null;
  sort?: number;
}

export interface SaveItemInput {
  id?: string | null;
  /** Required on insert; ignored on update (the row keeps its company). */
  companyId?: string;
  sku?: string;
  nameTr?: string;
  nameEn?: string | null;
  shortName?: string | null;
  type?: ItemType;
  status?: ItemStatus;
  itemGroupId?: string | null;
  baseUnit?: string;
  stockUnit?: string;
  barcode?: string | null;
  attributes?: Json;
  defaultSupplierId?: string | null;
  reorderPoint?: string | null;
  reorderQty?: string | null;
  safetyStockDays?: number | null;
  leadTimeDays?: number | null;
  minBatchQty?: string | null;
  planningHorizonDays?: number | null;
  isLotTracked?: boolean;
  lotSeries?: string | null;
  shelfLifeDays?: number | null;
  hsCode?: string | null;
  countryOfOrigin?: string | null;
  imagePath?: string | null;
  notes?: string | null;
  /**
   * On insert only: "napkin" or "bobbin" lets the RPC build the UoM ladder from
   * `settings.packaging`, so the form does not have to compute 800 adet/koli.
   */
  uomPreset?: 'napkin' | 'bobbin' | null;
  /** Replaces the ladder; leave undefined to keep the stored rows. */
  uoms?: ItemUomInput[];
  /**
   * Bought items only: the manually entered standard cost, already converted to
   * the base currency (E14 — the form shows the supplier currency and the rate,
   * the stored value is ₺).
   */
  cost?: { standardCost: string; unit?: string | null; note?: string | null } | null;
}

/**
 * `save_item` patches by key presence (`p_item ? 'sku'`), so a key the caller
 * left `undefined` must not be sent at all — sending it as null would wipe the
 * stored value. Only the fields actually set reach the payload.
 */
function payloadFrom(pairs: [string, unknown, ((v: string) => string)?][]): Record<string, Json> {
  const out: Record<string, Json> = {};
  for (const [key, value, transform] of pairs) {
    if (value === undefined) continue;
    out[key] = value === null ? null : transform ? transform(value as string) : (value as Json);
  }
  return out;
}

export async function saveItem(client: AslenClient, input: SaveItemInput): Promise<string> {
  const qty = (v: string) => toDb(v, 3);
  const item = payloadFrom([
    ['id', input.id ?? undefined],
    ['company_id', input.companyId],
    ['sku', input.sku],
    ['name_tr', input.nameTr],
    ['name_en', input.nameEn],
    ['short_name', input.shortName],
    ['type', input.type],
    ['status', input.status],
    ['item_group_id', input.itemGroupId],
    ['base_unit', input.baseUnit],
    ['stock_unit', input.stockUnit],
    ['barcode', input.barcode],
    ['attributes', input.attributes],
    ['default_supplier_id', input.defaultSupplierId],
    ['reorder_point', input.reorderPoint, qty],
    ['reorder_qty', input.reorderQty, qty],
    ['safety_stock_days', input.safetyStockDays],
    ['lead_time_days', input.leadTimeDays],
    ['min_batch_qty', input.minBatchQty, qty],
    ['planning_horizon_days', input.planningHorizonDays],
    ['is_lot_tracked', input.isLotTracked],
    ['lot_series', input.lotSeries],
    ['shelf_life_days', input.shelfLifeDays],
    ['hs_code', input.hsCode],
    ['country_of_origin', input.countryOfOrigin],
    ['image_path', input.imagePath],
    ['notes', input.notes],
    ['uom_preset', input.uomPreset],
  ]);

  const uoms =
    input.uoms?.map((u) => ({
      unit: u.unit,
      // factors are rates, not amounts: numeric(18,6)
      factor_to_base: toDb(u.factorToBase, 6),
      is_purchase_default: u.isPurchaseDefault ?? false,
      is_sales_default: u.isSalesDefault ?? false,
      is_order_multiple: u.isOrderMultiple ?? false,
      is_countable: u.isCountable ?? true,
      length_cm: u.lengthCm ? toDb(u.lengthCm, 3) : null,
      width_cm: u.widthCm ? toDb(u.widthCm, 3) : null,
      height_cm: u.heightCm ? toDb(u.heightCm, 3) : null,
      gross_weight_kg: u.grossWeightKg ? toDb(u.grossWeightKg, 3) : null,
      barcode: u.barcode || null,
      sort: u.sort ?? 0,
    })) ?? null;

  const cost = input.cost ? { standard_cost: toDb(input.cost.standardCost, 6), unit: input.cost.unit ?? null, note: input.cost.note ?? null } : null;
  return unwrap(await client.rpc('save_item', { p_item: item, p_uoms: uoms, p_cost: cost })) as string;
}

/**
 * W-04 bulk "Pasife al" / "Aktifleştir". Returns one row per id: an item that is
 * a component of an active BOM is refused with its reason, and the caller shows
 * the partial result rather than assuming everything went through.
 */
export async function setItemsStatus(client: AslenClient, ids: string[], status: ItemStatus) {
  return unwrap(await client.rpc('set_items_status', { p_ids: ids, p_status: status }));
}

/**
 * W-04 "Nitelik ata": a jsonb merge over the selected items; a null value removes the key.
 * `itemGroupId` assigns a group, `null` takes the group off (migration 0008 separates the two —
 * a plain null used to mean "leave it alone", so clearing silently did nothing).
 */
export async function bulkPatchItemAttributes(client: AslenClient, ids: string[], patch: Json, itemGroupId?: string | null): Promise<number> {
  // p_clear_group only exists from migration 0008 on, so it travels only when it is actually
  // needed; every other call keeps working against a database that has not had 0008 yet.
  const args = itemGroupId === null
    ? { p_ids: ids, p_patch: patch, p_clear_group: true }
    : { p_ids: ids, p_patch: patch, p_item_group_id: itemGroupId ?? undefined };
  return unwrap(await client.rpc('bulk_patch_item_attributes', args as never)) as number;
}

/**
 * Files a barcode on the item or on one of its UoMs (D-list addition).
 * Barcodes are unique across both levels within a company, which is why this is
 * an RPC and not an update.
 */
export async function setItemBarcode(client: AslenClient, itemId: string, code: string, unit?: string | null) {
  return unwrap(await client.rpc('set_item_barcode', { p_item_id: itemId, p_code: code, p_unit: unit ?? undefined }));
}

// ── item groups ─────────────────────────────────────────────────────────────
export async function listItemGroups(client: AslenClient, companyId: string): Promise<ItemGroup[]> {
  return unwrap(await client.from('item_groups').select('*').eq('company_id', companyId).is('deleted_at', null).order('sort').order('name'));
}

export async function upsertItemGroup(client: AslenClient, row: TablesInsert<'item_groups'>): Promise<ItemGroup> {
  return unwrap(await client.from('item_groups').upsert(row).select().single());
}

/** Soft delete: items keep their group_id history, the group leaves the pickers. */
export async function archiveItemGroup(client: AslenClient, id: string) {
  unwrap(await client.from('item_groups').update({ deleted_at: new Date().toISOString() }).eq('id', id).select('id'));
}

// ── product tree (BOM) ──────────────────────────────────────────────────────
export type BomWithCounts = Bom & { lines: { count: number }[]; operations: { count: number }[] };

/** Every version of one item's tree, newest first — the "Sürüm geçmişi" list. */
export async function listItemBoms(client: AslenClient, itemId: string) {
  return unwrap(
    await client
      .from('boms')
      .select('*, lines:bom_lines(count), operations:bom_operations(count)')
      .eq('item_id', itemId)
      .is('deleted_at', null)
      .order('version', { ascending: false }),
  ) as unknown as BomWithCounts[];
}

export type BomDetail = Bom & {
  lines: (BomLine & { component: Pick<Item, 'id' | 'sku' | 'name_tr' | 'short_name' | 'type' | 'base_unit' | 'attributes'> | null })[];
  operations: BomOperation[];
};

export async function getBom(client: AslenClient, bomId: string): Promise<BomDetail> {
  return unwrap(
    await client
      .from('boms')
      .select(
        '*, lines:bom_lines(*, component:items(id, sku, name_tr, short_name, type, base_unit, attributes)), operations:bom_operations(*)',
      )
      .eq('id', bomId)
      .order('sort', { referencedTable: 'bom_lines' })
      .order('seq', { referencedTable: 'bom_operations' })
      .single(),
  ) as unknown as BomDetail;
}

/** The exploded tree behind the W-05 editor (sub-BOMs resolved, quantities scaled). */
export async function bomTree(client: AslenClient, bomId: string, maxDepth?: number) {
  return unwrap(await client.rpc('fn_bom_tree', { p_bom_id: bomId, p_max_depth: maxDepth }));
}

/**
 * The cost panel. `seesCosts` roles only — for anyone else the function returns
 * nulls (and `fn_bom_cost` itself refuses), so the panel is hidden by role
 * before this is ever called. Quantities are **gross** (C62).
 */
export async function bomCost(client: AslenClient, bomId: string): Promise<Json> {
  return unwrap(await client.rpc('fn_bom_cost', { p_bom_id: bomId })) as Json;
}

/** New draft at `max(version) + 1`, copying from `fromBomId` (default: the active one). */
export async function createBomVersion(client: AslenClient, itemId: string, fromBomId?: string | null): Promise<string> {
  return unwrap(await client.rpc('create_bom_version', { p_item_id: itemId, p_from_bom_id: fromBomId ?? undefined })) as string;
}

export interface BomDraftLineInput {
  id?: string | null;
  componentItemId: string;
  qtyPer: string;
  unit?: string | null;
  scrapPct?: string | null;
  isOptional?: boolean;
  operationSeq?: number | null;
  note?: string | null;
  sort?: number;
}

export interface BomDraftOperationInput {
  id?: string | null;
  seq: number;
  name: string;
  opType?: string;
  workCenterId?: string | null;
  machineId?: string | null;
  setupMinutes?: string | null;
  minutesPer1000?: string | null;
  scrapPct?: string | null;
}

export interface BomDraftInput {
  header?: { routingType?: string | null; baseQty?: string; baseUnit?: string; notes?: string | null; changeNote?: string | null };
  lines?: BomDraftLineInput[];
  operations?: BomDraftOperationInput[];
}

/**
 * Replaces the draft's lines and operations in one transaction. Returns the
 * warnings the server found (today: a component scrap that disagrees with the
 * sum of the operation scraps), which the editor shows without blocking.
 */
export async function saveBomDraft(client: AslenClient, bomId: string, draft: BomDraftInput): Promise<Json> {
  const header = draft.header
    ? {
        ...(draft.header.routingType !== undefined ? { routing_type: draft.header.routingType } : {}),
        ...(draft.header.baseQty !== undefined ? { base_qty: toDb(draft.header.baseQty, 3) } : {}),
        ...(draft.header.baseUnit !== undefined ? { base_unit: draft.header.baseUnit } : {}),
        ...(draft.header.notes !== undefined ? { notes: draft.header.notes } : {}),
        ...(draft.header.changeNote !== undefined ? { change_note: draft.header.changeNote } : {}),
      }
    : null;
  const lines =
    draft.lines?.map((l, i) => ({
      id: l.id ?? null,
      sort: l.sort ?? i + 1,
      component_item_id: l.componentItemId,
      qty_per: toDb(l.qtyPer, 6),
      unit: l.unit ?? null,
      scrap_pct: l.scrapPct ? toDb(l.scrapPct, 3) : '0',
      is_optional: l.isOptional ?? false,
      operation_seq: l.operationSeq ?? null,
      note: l.note ?? null,
    })) ?? null;
  const ops =
    draft.operations?.map((o) => ({
      id: o.id ?? null,
      seq: o.seq,
      name: o.name,
      op_type: o.opType ?? 'other',
      work_center_id: o.workCenterId ?? null,
      machine_id: o.machineId ?? null,
      setup_minutes: o.setupMinutes ? toDb(o.setupMinutes, 3) : '0',
      minutes_per_1000: o.minutesPer1000 ? toDb(o.minutesPer1000, 3) : '0',
      scrap_pct: o.scrapPct ? toDb(o.scrapPct, 3) : '0',
    })) ?? null;
  return unwrap(await client.rpc('save_bom_draft', { p_bom_id: bomId, p_header: header, p_lines: lines, p_ops: ops })) as Json;
}

/** draft → active; the previous active version is archived in the same transaction. */
export async function activateBom(client: AslenClient, bomId: string, changeNote: string, updateStandard = true): Promise<Json> {
  return unwrap(await client.rpc('activate_bom', { p_bom_id: bomId, p_change_note: changeNote, p_update_standard: updateStandard })) as Json;
}

/**
 * Copies a tree onto another item as a **draft**. With `swapVariants`, each
 * component that belongs to a group whose `variant_keys` contains `colour` is
 * replaced by the sibling in the target item's colour.
 */
export async function copyBom(client: AslenClient, sourceBomId: string, targetItemId: string, swapVariants = false): Promise<string> {
  return unwrap(
    await client.rpc('copy_bom', { p_source_bom_id: sourceBomId, p_target_item_id: targetItemId, p_swap_variants: swapVariants }),
  ) as string;
}

export async function deleteBomDraft(client: AslenClient, bomId: string) {
  const { error } = await client.rpc('delete_bom_draft', { p_bom_id: bomId });
  if (error) throw toAppError(error);
}

// ── costs ───────────────────────────────────────────────────────────────────
/** W-05 → Maliyet. `seesCosts` roles only; the RPC refuses anyone else. */
export async function itemCostCard(client: AslenClient, itemId: string): Promise<Json> {
  return unwrap(await client.rpc('item_cost_card', { p_item_id: itemId })) as Json;
}

/** Freezes the active tree's roll-up into `item_costs` (+ history). */
export async function recomputeStandardCost(client: AslenClient, itemId: string, note?: string | null): Promise<Json> {
  return unwrap(await client.rpc('recompute_standard_cost', { p_item_id: itemId, p_note: note ?? undefined })) as Json;
}

/** Manual standard cost for a bought item, already in the base currency (E14). */
export async function setStandardCost(client: AslenClient, itemId: string, cost: string, unit?: string | null, note?: string | null) {
  return unwrap(await client.rpc('set_standard_cost', { p_item_id: itemId, p_cost: Number(toDb(cost, 6)), p_unit: unit ?? undefined, p_note: note ?? undefined }));
}

// ── item detail: stock, lots, movements (read-only; the engine owns the writes) ──
export type ItemStockRow = Views<'v_item_stock_by_location'>;
export type ItemLotRow = Views<'v_stock_lots'>;
export type ItemMovementRow = Views<'v_stock_ledger'>;

/**
 * W-05 › Stok, left card. One row per location with the lots summed; zero rows
 * exist in the view and are dropped here, because "0 koli on shelf A5" is noise
 * on an item card (W-06 is where an empty location still matters).
 */
export async function listItemStockByLocation(client: AslenClient, itemId: string): Promise<ItemStockRow[]> {
  const rows = unwrap(await client.from('v_item_stock_by_location').select('*').eq('item_id', itemId).order('location_code'));
  return rows.filter((r) => Number(r.qty_base ?? 0) !== 0);
}

/** W-05 › Stok, right card: lot × location with the "Kaynak" label the view builds. */
export async function listItemLots(client: AslenClient, itemId: string): Promise<ItemLotRow[]> {
  return unwrap(await client.from('v_stock_lots').select('*').eq('item_id', itemId).order('received_at', { ascending: false }).order('lot_no'));
}

/**
 * W-05 › Hareketler: the last movements of one item, newest first. The full
 * ledger with its filters is W-07; this is the item-scoped slice both screens
 * read from the same view.
 */
export async function listItemMovements(client: AslenClient, itemId: string, limit = 50): Promise<ItemMovementRow[]> {
  return unwrap(
    await client.from('v_stock_ledger').select('*').eq('item_id', itemId).order('occurred_at', { ascending: false }).order('no', { ascending: false }).limit(limit),
  );
}
